import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, autoUpdate, offset, flip, shift, useDismiss, useRole, useClick, useInteractions } from '@floating-ui/react';
import { MoreHorizontal, Calendar as CalendarIcon } from 'lucide-react';
import SectionHeader from '../../components/shared/ui/SectionHeader';
import SectionContainer from '../../components/shared/ui/SectionContainer';
import VirtualizedSpendTable from '../../components/shared/ui/VirtualizedSpendTable';
import { LockedTableHeader } from '../../components/shared/ui/LockedTableHeader';
import { LockedInputRow } from '../../components/shared/ui/LockedInputRow';
import { sortCampaignTransactions, type SortOrderItem } from '../../utils/sorting';
import AnimatedCounter from '../../components/shared/ui/AnimatedCounter';
import { showInfoToast } from '../../hooks/useToast';

// Lazy load Calendar component with error handling
// Note: Errors are logged as debug only - fallback handles gracefully so users never see errors
const Calendar = lazy(() => 
  import('../../components/shared/ui/Calendar').catch((error) => {
    // Only log in dev mode - fallback component handles this gracefully
    if (import.meta.env.DEV) {
      logger.debug('Calendar lazy load failed, using fallback', { error }, 'CampaignSpend');
    }
    // Return a fallback component
    return { 
      default: () => (
        <div className="p-4 text-center">
          <p className="text-sm text-textMuted">Calendar failed to load</p>
        </div>
      )
    };
  })
);
import { useSnapshotScope } from '../../contexts/SnapshotScope';
import { useSimpleSnapshot } from '../../hooks/useSimpleSnapshot';
import { useTableNavigation } from '../../hooks/useTableNavigation';
import ExportButton from '../../components/shared/ui/ExportButton';
import { formatTableDataForExport } from '../../utils/exportUtils';
import { logger } from '../../utils/logger';
import { parseLocalDate, formatYMD, todayYMD } from '../../utils/datePlain';
import { getCurrentWeekKey, getWeekStartDateFromKey } from '../../utils/weekKeyUtils';
import { ALLOW_PAST_WEEK_EDITS } from '../../config/auth';

// Safety assertion for date handling (dev only)
if (import.meta.env.DEV) {
  const test = '2025-10-16';
  const parsedDate = parseLocalDate(test);
  if (parsedDate) {
    formatYMD(parsedDate);
  }
}

import { CampaignSpendViewModel, CampaignTransaction } from '../../types';
import { useSortableTable } from '../../hooks/useSortableTable';
import { Input, ConfirmationModal } from '../../components/shared/ui';
import Dropdown, { DropdownOption } from '../../components/shared/common/Dropdown';
import { formatSpend } from '../../utils/spendHelpers';
import { testId } from '../../utils/testId';
import { useUser } from '../../contexts/UserContext';
import { LoadErrorState } from '../../components/shared/ui/LoadErrorState';



const getSortStorageKey = (weekKey: string) => `dashboard-campaign-spend-sort-${weekKey}`;


type SpendViewMode = 'side-by-side' | 'combined' | 'bubble-ai' | 'bubble-mobile';

interface CampaignSpendProps {
  onChange?: (transactions: CampaignTransaction[]) => void;
  className?: string;
  viewMode?: SpendViewMode;
}

const CAMPAIGN_OPTIONS: DropdownOption[] = [
  { value: '', label: 'Select Campaign' },
  { value: 'Bubble AI', label: 'Bubble AI' },
  { value: 'Bubble Native Mobile', label: 'Bubble Native Mobile' }
];


const CampaignSpend: React.FC<CampaignSpendProps> = React.memo((props) => {
  const { workspaceId, periodKey } = useSnapshotScope();
  const { canEditSection, isActuallyAdmin } = useUser();
  const isAdmin = isActuallyAdmin; // Check actual role, not effective role (viewer mode)
  const viewMode = props.viewMode;
  
  // Check if current week, future week, or past week editing is allowed
  const currentWeekKey = getCurrentWeekKey();
  const currentWeekDate = getWeekStartDateFromKey(currentWeekKey);
  const periodDate = getWeekStartDateFromKey(periodKey);
  const isCurrentWeek = periodKey === currentWeekKey;
  const isFutureWeek = currentWeekDate && periodDate && periodDate.getTime() > currentWeekDate.getTime();
  const isPastWeek = currentWeekDate && periodDate && periodDate.getTime() < currentWeekDate.getTime();
  
  // Determine if user can edit this section
  const canEdit = canEditSection('campaignSpend');
  
  // Admins can always edit past weeks (for cumulative dashboard, mistake corrections, etc.)
  // Viewers can only edit current/future weeks (or past weeks if explicitly enabled)
  const isEditable = canEdit && (isCurrentWeek || isFutureWeek || (isPastWeek && (isAdmin || ALLOW_PAST_WEEK_EDITS)));
  
  // Use the simplified useSimpleSnapshot hook
  const { 
    data: rawTransactions, 
    save, 
    rootKey: sectionRootKey,
    isLoading,
    workspaceReady,
    loadError
  } = useSimpleSnapshot<CampaignTransaction[]>({
    workspaceId,
    section: 'campaignSpend',
    periodKey,
    defaultValue: []
  });

  // Show skeleton only for data area during initial load (input row always visible)
  // ✅ FIX: Check for undefined explicitly (empty array [] is valid data)
  // ✅ FIX: Check workspaceReady for consistency with NotesSection
  const showDataSkeleton = !workspaceReady || (isLoading && rawTransactions === undefined);
  const hasCachedData = rawTransactions !== undefined;
  const isRefreshing = isLoading && hasCachedData;

  // Table navigation for keyboard shortcuts
  const tableTransactions = rawTransactions || [];
  useTableNavigation({
    rows: tableTransactions.length + 1, // +1 for input row
    cols: 4, // creator, date, spend, url
    onCellFocus: (row, col) => {
      // Focus the appropriate input field
      const cellSelector = `input[data-row="${row}"][data-col="${col}"]`;
      const element = document.querySelector(cellSelector) as HTMLInputElement;
      element?.focus();
    },
    onEnter: (row) => {
      // Commit changes on Enter
      if (row === tableTransactions.length) {
        // Input row - add new transaction
        handleAddRow();
      }
    },
    onEscape: () => {
      // Cancel editing
      setEditingRow(null);
    }
  });
  
  // Track if cleanup has already run (prevent aggressive cleanup on every change)
  const hasCleanedRef = useRef(false);
  
  // Reset cleanup flag when week changes (allow cleanup once per week)
  useEffect(() => {
    hasCleanedRef.current = false;
  }, [periodKey]);
  
  // Auto-save is handled by useSimpleSnapshot hook
  
  // Helper: treat as "has something" so we don't add blank rows
  const hasAnyInput = (r: CampaignTransaction) =>
    (r.creator?.trim() ?? '') !== '' ||
    (r.campaign?.trim() ?? '') !== '' ||
    (r.date?.trim() ?? '') !== '' ||
    Number(r.spend) > 0 ||
    (r.url?.trim() ?? '') !== '' ||
    (r.notes?.trim() ?? '') !== '';

  // Helper: check if row is complete and should be shown to viewers
  const isRowComplete = (r: CampaignTransaction) =>
    (r.creator?.trim() ?? '') !== '' &&
    (r.campaign?.trim() ?? '') !== '' &&
    (r.date?.trim() ?? '') !== '' &&
    Number(r.spend ?? 0) > 0;

  // Permanent input bar for admins (not saved until user fills it out)
  const [inputRow, setInputRow] = useState<CampaignTransaction>({
    id: -1, // Negative ID to distinguish from saved rows
    creator: '',
    campaign: '',
    spend: 0,
    platform: '',
    date: todayYMD(),
    notes: ''
  });

  // Keep the input row campaign aligned with the current filter to avoid surprises.
  useEffect(() => {
    if (viewMode === 'bubble-ai' && !inputRow.campaign) {
      setInputRow(prev => ({ ...prev, campaign: 'Bubble AI' }));
    }
    if (viewMode === 'bubble-mobile' && !inputRow.campaign) {
      setInputRow(prev => ({ ...prev, campaign: 'Bubble Native Mobile' }));
    }
  }, [viewMode, inputRow.campaign]);

  // Saved transactions (displayed below input bar)
  const [transactions, setTransactions] = useState<CampaignTransaction[]>([]);
  const prevComputedRef = useRef<CampaignTransaction[]>([]);
  
  // Memoize the computation to prevent redundant calculations
  const computedTransactions = useMemo(() => {
    // Filter out deleted items first (forward-only deletion preserves history)
    const visibleTransactions = (rawTransactions || []).filter((row: CampaignTransaction) => !row.deleted);
    
    // Viewers see only complete, committed rows with spend > 0
    if (!canEdit) {
      const committedRows = visibleTransactions.filter(r =>
        isRowComplete(r) && Number(r.spend ?? 0) > 0
      );
      return committedRows;
    }
    
    // Admins see all visible transactions (input bar is separate)
    if (rawTransactions === undefined) {
      return [];
    }
    
    return visibleTransactions;
  }, [rawTransactions, canEdit]);
  
  useEffect(() => {
    // Early return if computed value hasn't changed (prevents unnecessary setState)
    // Compare by reference first (fast path), then deep equality if needed
    if (prevComputedRef.current === computedTransactions) {
      return;
    }
    // Deep equality check using JSON.stringify (acceptable for this use case)
    if (JSON.stringify(prevComputedRef.current) === JSON.stringify(computedTransactions)) {
      return;
    }
    
    // Value changed - update state and ref
    prevComputedRef.current = computedTransactions;
    setTransactions(computedTransactions);
  }, [computedTransactions]);

  // One-time cleanup: remove incomplete rows (admins only)
  // ✅ FIX: Only run once on initial load, not on every change
  // This prevents rows from being deleted when user clears spend amount
  useEffect(() => {
    if (!isEditable) return;
    if (!Array.isArray(rawTransactions)) return;
    if (hasCleanedRef.current) return; // Already cleaned up
    
    const cleaned = rawTransactions.filter(isRowComplete);
    if (cleaned.length !== rawTransactions.length) {
      hasCleanedRef.current = true; // Mark as cleaned
      save(cleaned);
    } else if (rawTransactions.length > 0) {
      // If all rows are complete, mark as cleaned to prevent future runs
      hasCleanedRef.current = true;
    }
  }, [canEdit, rawTransactions, save]);

  // Immediate saving to maintain data consistency
  
  // Empty row creation is handled in the useEffect above to avoid race conditions
  
  // Use shared sortable table hook
  const { sortOrder, setSortOrder, handleResetSort } = useSortableTable(
    getSortStorageKey(periodKey), 'date','desc'
  );

  // Update parent component when transactions change
  useEffect(() => {
    if (props.onChange) {
      const safeTransactions = Array.isArray(transactions) ? transactions : [];
      props.onChange(safeTransactions);
    }
  }, [transactions, props.onChange]);
  
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  // Keyboard navigation state
  const [focusedCell, setFocusedCell] = useState<{ rowIdx: number; colIdx: number } | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | HTMLSelectElement | null>>({});
  // State for spend input focus
  const [focusedSpendRow, setFocusedSpendRow] = useState<number | null>(null);
  // State for row errors
  const [rowErrors, setRowErrors] = useState<Record<number, { campaign?: boolean }>>({});
  
  // State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; rowId: number | null; rowData: CampaignSpendViewModel | null }>({
    show: false,
    rowId: null,
    rowData: null
  });

  // Calendar state
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarRowId, setCalendarRowId] = useState<number | null>(null);
  const dateInputRef = useRef<HTMLSpanElement>(null);
  // ✅ FIX: Use ref map to track all date inputs for proper calendar positioning
  const dateInputRefs = useRef<Map<number, HTMLSpanElement>>(new Map());

  // Floating UI setup for calendar
  const { refs, floatingStyles, context } = useFloating({
    open: showCalendar,
    onOpenChange: setShowCalendar,
    placement: 'bottom-start',
    strategy: 'fixed', // ensures popper isn't affected by transformed ancestors
    middleware: [
      offset(8), // reduced from 15 to 8 for closer positioning
      flip({
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
        crossAxis: true
      }),
      shift({ padding: 10 }) // reduced from 20 to 10
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getFloatingProps, getReferenceProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // Create stable refs for each campaign dropdown button
  const campaignDropdownRefs = useRef<Array<React.RefObject<HTMLButtonElement | null>>>([]);
  
  // Ensure refs are stable and don't get recreated on every render
  useEffect(() => {
    const currentLength = campaignDropdownRefs.current.length;
    const neededLength = transactions.length;
    
    if (currentLength < neededLength) {
      // Add new refs if needed
      for (let i = currentLength; i < neededLength; i++) {
        campaignDropdownRefs.current[i] = React.createRef<HTMLButtonElement>();
      }
    } else if (currentLength > neededLength) {
      // Trim excess refs if we have too many
      campaignDropdownRefs.current = campaignDropdownRefs.current.slice(0, neededLength);
    }
  }, [transactions.length]);


  // Focus the correct input when focusedCell changes
  useEffect(() => {
    if (focusedCell && canEdit) {
      const key = `${focusedCell.rowIdx}-${focusedCell.colIdx}`;
      const el = inputRefs.current[key];
      if (el) {
        el.focus();
        if (el instanceof HTMLInputElement) el.select();
      }
    }
  }, [focusedCell, canEdit]);


  // isRowComplete is now defined at the top as a const arrow function

  // Separate complete and incomplete rows
  const completeRows = useMemo(() => {
    const safeTransactions = Array.isArray(transactions) ? transactions : [];
    return safeTransactions.filter((row: CampaignTransaction) => isRowComplete(row));
  }, [transactions]);

  const incompleteRows = useMemo(() => {
    const safeTransactions = Array.isArray(transactions) ? transactions : [];
    return safeTransactions.filter((row: CampaignTransaction) => !isRowComplete(row));
  }, [transactions]);

  // Calculate total spend
  const totalSpend = useMemo(() => {
    return completeRows.reduce((sum: number, row: CampaignTransaction) => {
      const spendValue = row.spend || 0;
      return sum + spendValue;
    }, 0);
  }, [completeRows]);

  // Handlers
  const handleAddRow = () => {
    logger.debug('handleAddRow called', { canEdit, isEditable }, 'CampaignSpend');
    if (!isEditable) {
      logger.debug('Add blocked - isEditable is false', undefined, 'CampaignSpend');
      return;
    }
    
    const newRow: CampaignTransaction = {
      id: Date.now(),
      creator: '',
      date: todayYMD(),
      spend: 0,
      campaign: '',
      url: ''
    };
    
    logger.debug('Creating new row', newRow, 'CampaignSpend');
    
    const safePrev = Array.isArray(transactions) ? transactions : [];
    const newTransactions = [...safePrev, newRow];
    logger.debug('Updated transactions array', { count: newTransactions.length }, 'CampaignSpend');
    save(newTransactions);
    
    setEditingRow(newRow.id);
  };
  
  // Handle input bar submission - create new row from input bar
  const handleInputBarSubmit = useCallback(() => {
    if (!isEditable) return;

    // Only avoid truly empty rows
    if (!hasAnyInput(inputRow)) return;

    const newRow: CampaignTransaction = { ...inputRow, id: Date.now() };
    const safePrev = Array.isArray(transactions) ? transactions : [];
    save([...safePrev, newRow]);

    // If user adds a row for a different campaign than the current filter, inform them.
    if (
      (viewMode === 'bubble-ai' && newRow.campaign !== 'Bubble AI') ||
      (viewMode === 'bubble-mobile' && newRow.campaign !== 'Bubble Native Mobile')
    ) {
      showInfoToast(
        'Added to other campaign',
        `${newRow.campaign || 'Unspecified'} added and hidden by current filter`
      );
    }

    // reset the input bar
    setInputRow({
      id: -1,
      creator: '',
      campaign: '',
      spend: 0,
      platform: '',
      date: todayYMD(),
      notes: ''
    });
  }, [isEditable, inputRow, transactions, save]);
  
  // Handle input bar field changes
  const handleInputBarChange = useCallback((field: keyof CampaignTransaction, value: string | number | undefined) => {
    setInputRow(prev => ({ ...prev, [field]: value }));
    
    // Auto-submit when user tabs out of last field or presses Enter
    // This will be triggered by onBlur or onKeyDown events
  }, []);

  // Handle date changes for existing rows
  const handleDateChange = useCallback((id: number, value: string) => {
    if (!isEditable) return;
    const safePrev = Array.isArray(transactions) ? transactions : [];
    const updated = safePrev.map((row: CampaignTransaction) =>
      row.id === id ? { ...row, date: value } : row
    );
    save(updated);
  }, [isEditable, transactions, save]);

  // Calendar handlers
  const handleDateClick = useCallback((rowId: number) => {
    setCalendarRowId(rowId);
    setShowCalendar(true);
    // ✅ FIX 4: Update Floating UI reference synchronously, use requestAnimationFrame for position recalculation
    const el = dateInputRefs.current.get(rowId);
    if (el) {
      refs.setReference(el);
      // Use requestAnimationFrame for position recalculation (more reliable than setTimeout)
      requestAnimationFrame(() => {
        if (context) {
          context.update();
        }
      });
    }
  }, [refs, context]);

  const handleCalendarDateSelect = useCallback((date: Date) => {
    if (calendarRowId === null) return;
    
    
    const dateString = formatYMD(date);
    
    if (calendarRowId === -1) {
      // Input bar
      handleInputBarChange('date', dateString);
    } else {
      // Existing row
      handleDateChange(calendarRowId, dateString);
    }
    
    setShowCalendar(false);
    setCalendarRowId(null);
  }, [calendarRowId, handleInputBarChange, handleDateChange]);

  const handleDeleteRow = (id: number) => {
    if (!isEditable) return;
    const rowToDelete = transactions.find((row: CampaignTransaction) => row.id === id);
    if (rowToDelete) {
      setDeleteConfirm({
        show: true,
        rowId: id,
        rowData: {
          id: String(rowToDelete.id),
          date: String(rowToDelete.date),
          amount: Number(rowToDelete.spend),
          campaign: String(rowToDelete.campaign)
        }
      });
    }
  };
  
  const confirmDelete = () => {
    // ✅ Type safety: Explicit null check before accessing deleteConfirm.rowId
    if (deleteConfirm.rowId !== null && deleteConfirm.rowId !== undefined) {
      const safePrev = Array.isArray(rawTransactions) ? rawTransactions : [];
      // Mark as deleted instead of removing from array (forward-only deletion)
      // This preserves historical data in past weeks while preventing forward-fill to future weeks
      const updated = safePrev.map((row: CampaignTransaction) =>
        row.id === deleteConfirm.rowId 
          ? { ...row, deleted: true } 
          : row
      );
      save(updated);
      // ✅ Type safety: Check editingRow is not null before comparison
      if (editingRow !== null && editingRow === deleteConfirm.rowId) {
        setEditingRow(null);
      }
      setDeleteConfirm({ show: false, rowId: null, rowData: null });
    }
  };
  
  const cancelDelete = () => {
    setDeleteConfirm({ show: false, rowId: null, rowData: null });
  };

  // Keyboard shortcuts for delete confirmation modal
  useEffect(() => {
    if (!deleteConfirm.show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmDelete();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelDelete();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteConfirm.show]);

  // Type-safe handleChange
  const handleChange = (id: number, key: keyof CampaignTransaction, value: string | number) => {
    if (!isEditable) return;
    const safePrev = Array.isArray(transactions) ? transactions : [];
    const updated = safePrev.map((row: CampaignTransaction) =>
      row.id === id ? { ...row, [key]: typeof value === 'string' ? value : String(value) } : row
    );
    save(updated);
  };


  const handleSpendChange = (id: number, value: string) => {
    if (!isEditable) return;
    const numericValue = parseFloat(value.replace(/[^\d.]/g, '')) || 0;
    const safePrev = Array.isArray(rawTransactions) ? rawTransactions : [];
    const updated = safePrev.map((row: CampaignTransaction) =>
      row.id === id ? { ...row, spend: numericValue } : row
    );
    save(updated);
  };

  const handleRowClick = (rowId: number) => {
    if (!canEdit) return;
    setEditingRow(rowId);
  };

  const handleRowBlur = () => {};
  
  // Handle save feedback for inputs
  const handleSaveFeedback = (_rowId: number, _action: 'start' | 'success' | 'error') => {
    // This hook manages its own save states internally
  };

  // Helper to get column alignment
  const getColumnAlign = (columnKey: string): 'left' | 'center' | 'right' => {
    switch (columnKey) {
      case 'creator':
      case 'url':
        return 'left';
      case 'spend':
        return 'right';
      case 'date':
      case 'campaign':
      case 'actions':
      default:
        return 'center';
    }
  };

  // Table columns configuration
  const columns = [
    { key: 'creator', label: 'Creator', sortable: true, align: getColumnAlign('creator'), width: '15%' },
    { key: 'date', label: 'Date', sortable: true, align: getColumnAlign('date'), width: '12%' },
    { key: 'spend', label: 'Spend', sortable: true, align: getColumnAlign('spend'), width: '10%' },
    { key: 'campaign', label: 'Campaign', sortable: true, align: getColumnAlign('campaign'), width: '20%' },
    { key: 'url', label: 'URL', sortable: true, align: getColumnAlign('url'), width: '33%' },
            ...(canEdit ? [{ key: 'actions', label: 'Actions', sortable: false, align: getColumnAlign('actions'), width: '10%' }] : [])
  ];
  const editableKeys = ['creator', 'date', 'spend', 'campaign', 'url'];

  // Helper to get ref key
  const getRefKey = (rowIdx: number, colIdx: number) => `${rowIdx}-${colIdx}`;

  // Keyboard navigation helpers
  const getNextEditableCell = (rowIdx: number, colIdx: number, direction: 'right' | 'left' | 'down' | 'up' | 'tab' | 'enter') => {
    const editableColIdxs = columns.map((c, i) => editableKeys.includes(c.key) ? i : -1).filter(i => i !== -1);
    let nextRow = rowIdx;
    let nextCol = colIdx;
    if (direction === 'right' || direction === 'tab' || direction === 'enter') {
      const next = editableColIdxs.find(i => i > colIdx);
      if (next !== undefined) {
        nextCol = next;
      } else {
        nextRow = rowIdx + 1;
        nextCol = editableColIdxs[0];
      }
    } else if (direction === 'left') {
      const prev = [...editableColIdxs].reverse().find(i => i < colIdx);
      if (prev !== undefined) {
        nextCol = prev;
      } else {
        nextRow = rowIdx - 1;
        nextCol = editableColIdxs[editableColIdxs.length - 1];
      }
    } else if (direction === 'down') {
      nextRow = rowIdx + 1;
    } else if (direction === 'up') {
      nextRow = rowIdx - 1;
    }
    return { rowIdx: nextRow, colIdx: nextCol };
  };

  // Render cell content
  const renderCell = (row: CampaignTransaction, columnKey: string, rowIdx?: number, colIdx?: number) => {
    const isInputBar = row.id === -1; // Check if this is the permanent input bar
    const isEditing = editingRow === row.id || isInputBar; // Input bar is always in edit mode
    const isEditable = canEdit && editableKeys.includes(columnKey);
    const refKey = rowIdx !== undefined && colIdx !== undefined ? getRefKey(rowIdx, colIdx) : undefined;
    const rowError = rowErrors[row.id] || {};

    const handleCellKeyDown = (e: React.KeyboardEvent) => {
      if (!isEditable || !canEdit) return;
      const allRows = [...completeRows, ...incompleteRows];
      const rowCount = allRows.length;
      if (rowIdx === undefined || colIdx === undefined) return;
      
      // For campaign dropdown and date calendar, let ArrowDown/ArrowUp open them naturally
      const currentColumnKey = columns[colIdx]?.key;
      if (currentColumnKey === 'campaign' || currentColumnKey === 'date') {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          // Don't preventDefault - let the Dropdown/Calendar component handle it
          return;
        }
      }
      
      // Let Tab work naturally for navigation - don't preventDefault
      if (e.key === 'Tab') {
        return;
      }
      
      let direction: 'right' | 'left' | 'down' | 'up' | 'enter' | null = null;
      switch (e.key) {
        case 'ArrowRight': direction = 'right'; break;
        case 'ArrowLeft': direction = 'left'; break;
        case 'ArrowDown': direction = 'down'; break;
        case 'ArrowUp': direction = 'up'; break;
        case 'Enter': direction = 'enter'; break;
        default: break;
      }
      if (direction) {
        e.preventDefault();
        const { rowIdx: nextRow, colIdx: nextCol } = getNextEditableCell(rowIdx!, colIdx!, direction);
        if (columns[nextCol]?.key === 'campaign') {
          const dropdownButtonRef = campaignDropdownRefs.current[nextRow];
          if (dropdownButtonRef && dropdownButtonRef.current) {
            dropdownButtonRef.current.focus();
            setTimeout(() => dropdownButtonRef.current?.click(), 10);
          }
        } else {
          // Clamp to max editable index instead of colCount - 2
          const editableIdxs = columns
            .map((c, i) => (editableKeys.includes(c.key) ? i : -1))
            .filter(i => i !== -1);
          const maxEditableIdx = editableIdxs[editableIdxs.length - 1] ?? 0;
          setFocusedCell({
            rowIdx: Math.max(0, Math.min(rowCount - 1, nextRow)),
            colIdx: Math.max(0, Math.min(maxEditableIdx, nextCol)),
          });
        }
      }
    };

    switch (columnKey) {
      case 'creator':
        return (
          <div className="relative">
            <Input
              ref={el => { if (refKey) inputRefs.current[refKey] = el; }}
              data-row={rowIdx}
              data-col={colIdx}
              tabIndex={0}
              className={`w-full px-3 py-2 text-sm text-textPrimary
                ${isEditing ? 'bg-tableHover' : ''}
                transition-all duration-200
              `}
              value={row.creator}
              onChange={e => {
                if (isInputBar) {
                  handleInputBarChange('creator', e.target.value);
                } else {
                  handleChange(row.id, 'creator', e.target.value);
                  handleSaveFeedback(row.id, 'start');
                }
              }}
              onBlur={() => { /* no auto submit for input row */ if (!isInputBar) { handleRowBlur(); handleSaveFeedback(row.id, 'success'); } }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isInputBar) {
                    handleInputBarSubmit();
                  } else {
                    // For regular cells, move to next row
                    const allRows = [...completeRows, ...incompleteRows];
                    const rowCount = allRows.length;
                    if (rowIdx !== undefined) {
                      const nextRow = Math.min(rowCount - 1, rowIdx + 1);
                      setFocusedCell({ rowIdx: nextRow, colIdx: colIdx || 0 });
                    }
                  }
                  return;
                }
                handleCellKeyDown(e);
              }}
              disabled={!isEditable}
              placeholder="e.g. @username"
              onClick={() => !isInputBar && handleRowClick(row.id)}
              data-testid={`creator-input-${row.id}`}
            />
          </div>
        );
      case 'date':
        return (
          <div className="relative">
            <span
              ref={(el) => {
                // ✅ FIX 4: Store ref in map for this specific row
                if (el) {
                  dateInputRefs.current.set(row.id, el);
                } else {
                  dateInputRefs.current.delete(row.id);
                }
                // Always update Floating UI reference if this is the active calendar row
                if (calendarRowId === row.id && el) {
                  refs.setReference(el);
                  // Force position update
                  requestAnimationFrame(() => {
                    if (context) {
                      context.update();
                    }
                  });
                }
                dateInputRef.current = el;
              }}
              {...getReferenceProps()}
              onClick={(e) => {
                e.stopPropagation();
                handleDateClick(row.id);
              }}
              className={`w-full px-3 py-2 text-sm text-textPrimary cursor-pointer flex items-center gap-2
                ${isEditing ? 'bg-tableHover' : ''}
                transition-all duration-200
              `}
              style={{
                backgroundColor: 'var(--bg-elev)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
                opacity: 1,
                isolation: 'isolate'
              }}
              data-testid={`date-input-${row.id}`}
            >
              <CalendarIcon size={16} />
              {row.date ? (parseLocalDate(row.date)?.toLocaleDateString() || 'Invalid date') : 'Select date'}
            </span>
          </div>
        );
      case 'spend': {
        const spendFormat = formatSpend(row.spend);
        const isFocused = focusedSpendRow === row.id;
        return (
          <div className="relative">
            <Input
              ref={el => { if (refKey) inputRefs.current[refKey] = el; }}
              data-row={rowIdx}
              data-col={colIdx}
              tabIndex={0}
              type="text"
              className={`w-full px-3 py-2 text-sm text-textPrimary
                ${isEditing ? 'bg-tableHover' : ''}
                transition-all duration-200
                ${spendFormat.className}
              `}
              value={isFocused ? (row.spend === 0 ? '' : (row.spend || 0).toString()) : (row.spend === 0 ? '' : (row.spend != null && !isNaN(Number(row.spend)) ? Number(row.spend).toLocaleString() : ''))}
              onChange={e => {
                const raw = e.target.value.replace(/,/g, '');
                if (isInputBar) {
                  handleInputBarChange('spend', raw === '' ? 0 : parseFloat(raw) || 0);
                } else {
                  handleSpendChange(row.id, raw);
                  handleSaveFeedback(row.id, 'start');
                }
              }}
              onFocus={() => {
                setFocusedSpendRow(row.id);
                if (!isInputBar) {
                  handleRowClick(row.id);
                }
              }}
              onBlur={() => {
                setFocusedSpendRow(null);
                if (!isInputBar) {
                  handleRowBlur();
                  handleSaveFeedback(row.id, 'success');
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isInputBar) {
                    handleInputBarSubmit();
                  } else {
                    // For regular cells, move to next row
                    const allRows = [...completeRows, ...incompleteRows];
                    const rowCount = allRows.length;
                    if (rowIdx !== undefined) {
                      const nextRow = Math.min(rowCount - 1, rowIdx + 1);
                      setFocusedCell({ rowIdx: nextRow, colIdx: colIdx || 0 });
                    }
                  }
                  return;
                }
                handleCellKeyDown(e);
              }}
              disabled={!isEditable}
              placeholder="$0"
              data-testid={`spend-input-${row.id}`}
            />
          </div>
        );
      }
      case 'campaign':
        if (row.id !== -1 && row.campaign) {
          const isAI = row.campaign === 'Bubble AI';
          return (
            <span className={`mgz-pill ${isAI ? 'mgz-pill--ai' : 'mgz-pill--mobile'}`} style={{ whiteSpace: 'nowrap' }}>
              {row.campaign}
            </span>
          );
        }
        // fall back to Dropdown for input bar / incomplete rows
        return (
          <Dropdown
            ref={campaignDropdownRefs.current[rowIdx!]}
            value={row.campaign}
            className={`${isEditing ? 'bg-tableHover border-border' : 'bg-ctrl-bg'} rounded-lg border`}
            onChange={(value?: string) => {
              if (isInputBar) {
                handleInputBarChange('campaign', String(value ?? ''));
              } else {
                handleChange(row.id, 'campaign', String(value ?? ''));
                setRowErrors(prev => ({ ...prev, [row.id]: { ...prev[row.id], campaign: false } }));
              }
            }}
            onBlur={() => { /* no auto submit for input bar */ }}
            options={CAMPAIGN_OPTIONS}
            placeholder="Select Campaign"
            disabled={!canEdit}
            onFocus={() => !isInputBar && handleRowClick(row.id)}
            error={!!rowError.campaign}
            tooltip="Choose a campaign to attribute this spend"
            debug={false}
            testId={`campaign-dropdown-${rowIdx}`}
          />
        );
      case 'url':
        if (row.id !== -1 && row.url) {
          return (
            <a href={row.url} target="_blank" rel="noopener noreferrer">
              {row.url}
            </a>
          );
        }
        // else keep your existing Input for input bar / incomplete rows
        return (
          <div className="relative">
            <Input
              ref={el => { if (refKey) inputRefs.current[refKey] = el; }}
              data-row={rowIdx}
              data-col={colIdx}
              tabIndex={0}
              type="url"
              className={`w-full px-3 py-2 text-sm text-textPrimary transition-all duration-200 ${isEditing ? 'bg-tableHover' : ''}`}
              value={row.url || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (isInputBar) {
                  handleInputBarChange('url', String(e.target.value ?? ''));
                } else {
                  handleChange(row.id, 'url', String(e.target.value ?? ''));
                  setRowErrors(prev => ({ ...prev, [row.id]: { ...prev[row.id], campaign: false } }));
                  handleSaveFeedback(row.id, 'start');
                }
              }}
              onBlur={() => {
                if (!isInputBar) {
                  handleRowBlur();
                  handleSaveFeedback(row.id, 'success');
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isInputBar) {
                    handleInputBarSubmit();
                  } else {
                    // For regular cells, move to next row
                    const allRows = [...completeRows, ...incompleteRows];
                    const rowCount = allRows.length;
                    if (rowIdx !== undefined) {
                      const nextRow = Math.min(rowCount - 1, rowIdx + 1);
                      setFocusedCell({ rowIdx: nextRow, colIdx: colIdx || 0 });
                    }
                  }
                  return;
                }
                handleCellKeyDown(e);
              }}
              disabled={!isEditable}
              placeholder="https://..."
              onClick={() => !isInputBar && handleRowClick(row.id)}
              data-testid={`url-input-${row.id}`}
            />
          </div>
        );
      case 'actions': {
        const isInputBar = row.id === -1;

        if (isInputBar) {
          return (
            <div className="flex items-center justify-center gap-2 w-full">
              <button
                onClick={handleInputBarSubmit}
                className="px-3 py-1.5 text-sm font-medium text-textInverse bg-accent hover:bg-accentStrong rounded-lg transition-colors"
              >
                Add
              </button>
              <button
                onClick={() =>
                  setInputRow({
                    id: -1,
                    creator: '',
                    campaign: '',
                    spend: 0,
                    platform: '',
                    date: todayYMD(),
                    url: '',
                    notes: ''
                  })
                }
                className="px-3 py-1.5 text-sm font-medium text-textPrimary bg-card border border-border rounded-lg hover:bg-tableHover transition-colors"
              >
                Clear
              </button>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-center">
            <button
              className="text-textPrimary transition-colors p-2 rounded-lg hover:bg-tableHover"
              onClick={() => handleDeleteRow(row.id)}
              title="Delete transaction"
              aria-label="Delete transaction"
              data-testid={`delete-row-btn-${row.id}`}
            >
              <MoreHorizontal size={16} />
            </button>
          </div>
        );
      }
      default:
        return row[columnKey as keyof CampaignTransaction];
    }
  };

  // Custom multi-column sort logic for Campaign Spend
  // Sorting function moved to src/utils/sorting.ts
  
  // Prepare data for VirtualizedSpendTable (saved rows only - input row rendered separately)
  const savedRows = useMemo(() => {
    const sortedCompleteRows = sortCampaignTransactions(completeRows as unknown as Array<Record<string, unknown>>, sortOrder as unknown as SortOrderItem[]) as unknown as CampaignTransaction[];
    return [...sortedCompleteRows, ...incompleteRows];
  }, [completeRows, incompleteRows, sortOrder]);

  // Apply view filter to saved rows (display-only)
  const viewFilteredRows = useMemo(() => {
    if (!viewMode || viewMode === 'side-by-side' || viewMode === 'combined') return savedRows;
    if (viewMode === 'bubble-ai') return savedRows.filter(r => String(r.campaign) === 'Bubble AI');
    if (viewMode === 'bubble-mobile') return savedRows.filter(r => String(r.campaign) === 'Bubble Native Mobile');
    return savedRows;
  }, [savedRows, viewMode]);



  // Custom getTopItems for saved rows (input row not included)
  const getTopItems = useCallback((data: CampaignTransaction[], count: number) => {
    const safeData = Array.isArray(data) ? data : [];
    const completeData = safeData.filter((_, index: number) => index < completeRows.length);
    const incompleteData = safeData.slice(completeRows.length);
    if (isCollapsed) {
      const top = completeData.slice(0, count);
      // Always show the first incomplete row if present
      if (incompleteData.length > 0) {
        return [...top, incompleteData[0]];
      }
      return top;
    }
    return data;
  }, [isCollapsed, completeRows.length]);


  return (
    <SectionContainer 
      key={sectionRootKey}
      testId="section-campaign-spend" 
      aria-label="Campaign Spend section"
      variant="bare"
      className={`${props.className} ${hasCachedData ? 'animate-fadeIn' : ''} relative`}
    >
      {/* Error state if workspace failed to resolve */}
      {loadError && (
        <div className="px-6 py-6">
          <LoadErrorState
            error={loadError}
            onRetry={() => window.location.reload()}
            onRefresh={() => window.location.reload()}
            sectionName="Campaign Spend"
          />
        </div>
      )}
      {/* Header always visible - outside skeleton wrapper */}
      {!loadError && (
        <div className="w-full">
          <div className="bg-[var(--bg-elev)] rounded-[1.75rem] overflow-hidden overflow-x-hidden" data-section="campaign-spend">
          {/* Header Section with Total - Always rendered */}
          <div className="px-12 py-12 card-header">
            <SectionHeader title="Campaign Spend" href="https://docs.google.com/campaign-spend-master">
              {/* Action Buttons */}
              <div className="flex items-center gap-4">
                <ExportButton
                  data={formatTableDataForExport(
                    savedRows, // Only saved rows (input row not included)
                    columns,
                    (row, key) => {
                      const value = (row as Record<string, any>)[key];
                      if (key === 'spend' && typeof value === 'number') {
                        return value.toLocaleString();
                      }
                      if (key === 'date' && value) {
                        return parseLocalDate(value)?.toLocaleDateString() || value;
                      }
                      return value;
                    }
                  )}
                  filename="campaign-spend"
                  variant="csv"
                  showDropdown={false}
                  className="mr-2 bg-accent text-textInverse hover:bg-accentStrong"
                />
                <button
                  onClick={handleResetSort}
                  title="Reset sort"
                  {...testId('reset-sort-btn')}
              className="clickable-action-btn px-3 py-2 text-sm font-medium text-textPrimary bg-card border border-border rounded-lg hover:bg-tableHover transition-all duration-200 cursor-pointer"
                  aria-label="Reset table sort to default"
                >
                  ↺ Reset Sort
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsCollapsed(!isCollapsed);
                  }}
                  disabled={completeRows.length <= 5}
                  {...testId('toggle-collapse-btn')}
                  className={`clickable-show-more px-4 py-2.5 text-sm font-medium border theme-border rounded-lg transition-all duration-200 ${
                    completeRows.length <= 5
                      ? 'theme-text-muted theme-muted cursor-not-allowed'
                      : 'theme-text-light theme-surface hover:bg-[color-mix(in_srgb,var(--accent-primary)_12%,_transparent)] hover:theme-border cursor-pointer'
                  }`}
                  aria-label={isCollapsed ? `Show more campaign spend rows (${completeRows.length - 5} more)` : 'Show fewer campaign spend rows'}
                >
                  {isCollapsed ? `Show More${completeRows.length > 5 ? ` (${completeRows.length - 5} more)` : ''}` : 'Show Less'}
                </button>
              </div>
            </SectionHeader>
          
          {/* Total Spend Display */}
          <div className="mt-8">
            <AnimatedCounter
              value={totalSpend}
              duration={1.8}
              delay={0.2}
              prefix="$"
              size="hero"
              decimals={2}
            />
            <div className="text-sm text-textPrimary opacity-80 mt-2">Total Spend</div>
          </div>
        </div>

        {/* Table Section - Locked Header, Locked Input Row, Scrollable Data Rows */}
        <div className="px-4 py-3">
          {/* Input Row Section */}
          {canEdit && (
            <div className="mb-4">
              {/* Input Row Section Header */}
              <div className="mb-2.5">
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <span className="text-xs font-semibold theme-text uppercase tracking-wide">
                    Add New Transaction
                  </span>
                  <div className="flex-1 h-px bg-[var(--border-subtle)]"></div>
                </div>
              </div>
              {/* 1. LOCKED INPUT ROW - placed above data for quick entry */}
              <div className="border-2 border-dashed border-[var(--accent-primary)] rounded-lg bg-[color-mix(in_srgb,var(--accent-primary)_5%,_transparent)] p-1">
                <LockedInputRow
                  inputRow={inputRow}
                  columns={columns}
                  renderCell={(row, columnKey, rowIdx, colIdx) => renderCell(row, columnKey, rowIdx, colIdx)}
                />
              </div>
            </div>
          )}

          {/* 1. LOCKED TABLE HEADER - Always visible, not affected by sorting/scrolling */}
          <LockedTableHeader
            columns={columns}
            sortOrder={sortOrder}
            onSort={(field, event) => {
              const isShiftClick = event.shiftKey;
              let newOrder: SortOrderItem[];
              if (isShiftClick) {
                const existingIndex = sortOrder.findIndex(item => item.field === field);
                if (existingIndex > -1) {
                  const existingItem = sortOrder[existingIndex];
                  const updatedItem: SortOrderItem = { ...existingItem, direction: existingItem.direction === 'asc' ? 'desc' : 'asc' };
                  newOrder = sortOrder.map((item, index) => index === existingIndex ? updatedItem : item);
                } else {
                  newOrder = [...sortOrder, { field, direction: 'asc' as const }];
                }
              } else {
                const currentSort = sortOrder.find(item => item.field === field);
                if (currentSort) {
                  newOrder = [{ field, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' }];
                } else {
                  newOrder = [{ field, direction: 'asc' as const }];
                }
              }
              setSortOrder(newOrder);
            }}
            variant="magazine"
          />

          {/* 3. SCROLLABLE DATA CONTAINER - Only data rows scroll, fade in after loading */}
          {/* Fit-to-content: Only use fixed height when virtualization is needed (>20 rows) */}
          <div className="relative" style={viewFilteredRows.length > 20 ? { height: '400px' } : { minHeight: 'auto' }}>
            {showDataSkeleton ? (
              <div className="min-h-[200px] space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-[60px] bg-surface rounded animate-pulse"></div>
                ))}
              </div>
            ) : (
              <>
                {isRefreshing && (
                  <div className="absolute inset-0 bg-[var(--bg)] bg-opacity-50 z-10 flex items-center justify-center pointer-events-none">
                    <div className="opacity-75">
                      <div className="min-h-[200px] space-y-2">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={i} className="h-[60px] bg-surface rounded animate-pulse"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {/* Smooth fade-in animation for data rows only (300ms) */}
                <div className={`transition-opacity duration-300 ease-out ${hasCachedData ? 'opacity-100' : 'opacity-0'}`}>
                  <VirtualizedSpendTable
                    key={sectionRootKey}
                    title=""
                    data={viewFilteredRows}
                    columns={columns}
                    isCollapsed={isCollapsed}
                    showTotals={false}
                    sortOrder={sortOrder}
                    onSortOrderChange={(order: SortOrderItem[]) => setSortOrder(order)}
                    getTopItems={getTopItems}
                    renderCell={(row, columnKey, rowIdx, colIdx) => renderCell(row, columnKey, rowIdx, colIdx)}
                    rowHeight={60}
                    overscan={5}
                    containerHeight={savedRows.length > 20 ? 400 : undefined}
                    variant="magazine"
                  />
                </div>
              </>
            )}
          </div>
        </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirm.show}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          deleteConfirm.rowData 
            ? `Are you sure you want to delete this transaction?\n\nCampaign: ${deleteConfirm.rowData.campaign || 'Not specified'}\nAmount: $${deleteConfirm.rowData.amount != null ? Number(deleteConfirm.rowData.amount).toLocaleString() : '0'}`
            : 'Are you sure you want to delete this transaction?'
        }
        variant="destructive"
      />

      {/* Calendar Portal */}
      {showCalendar && createPortal(
        <div
          ref={refs.setFloating}
          data-floating-ui-portal
          style={{
            ...floatingStyles,
            zIndex: 11000,
            backgroundColor: 'var(--bg-elev)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            boxShadow: 'var(--shadow-strong)',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            opacity: 1,
            isolation: 'isolate'
          }}
          {...getFloatingProps()}
        >
          <Suspense fallback={<div className="p-4 text-textPrimary">Loading calendar...</div>}>
            <Calendar
              value={
                calendarRowId === -1
                  ? (inputRow.date ? parseLocalDate(inputRow.date) ?? undefined : undefined)
                  : calendarRowId !== null
                    ? parseLocalDate(transactions.find(t => t.id === calendarRowId)?.date || '') ?? undefined
                    : undefined
              }
              onChange={handleCalendarDateSelect}
              onClose={() => setShowCalendar(false)}
              showMonthYearDropdown={true}
            />
          </Suspense>
        </div>,
        document.body
      )}
    </SectionContainer>
  );
});

CampaignSpend.displayName = 'CampaignSpend';

export default CampaignSpend; 